import m, {Component, Vnode, VnodeDOM} from "mithril";
import BaseComponent from "../../BaseComponent";
import {ComponentContext} from "../../IComponent";
import {
    MediaPlayerActions,
    bindMediaPlayerActions,
    isUnsupportedMediaError,
    readMediaPlayerActions,
    renderMediaPlayerActions,
    renderUnsupportedNotice,
    showUnsupportedFormatDialog,
} from "../../MediaPlayer/mediaPlayerActions";

export default class VideoPlayer extends BaseComponent implements Component {

    private video: HTMLVideoElement | null = null;
    private src = '';
    private playing = false;
    private currentTime = 0;
    private duration = 0;
    private seeking = false;
    private fullscreen = false;
    private controlsVisible = true;
    private hideTimer: number | null = null;
    private filename = '';
    private unsupported = false;
    private playRequested = false;
    private dialogShown = false;
    private actions: MediaPlayerActions | null = null;

    constructor(
        $component: JQuery<HTMLElement>,
        context: ComponentContext
    ) {
        super($component,context);
    }

    public init(): void {
        const src = this.readAttribute('src');
        if (!src) {
            console.warn('VideoPlayer: missing data-src');
            return;
        }
        this.src = src;
        this.filename = this.readAttribute('filename') ?? '';
        this.actions = readMediaPlayerActions(this.$component[0]);
        document.addEventListener('fullscreenchange', this.onFullscreenChange);
        m.mount(this.$component[0], this);

        if (this.actions) {
            bindMediaPlayerActions(this.$component[0]);
        }
    }

    public beforeRemove(): void {
        document.removeEventListener('fullscreenchange', this.onFullscreenChange);
        this.clearHideTimer();
        if (this.video) {
            this.video.pause();
            this.video.removeAttribute('src');
            this.video.load();
            this.video = null;
        }
        if (this.$component[0]) {
            m.mount(this.$component[0], null);
        }
    }

    private onFullscreenChange = (): void => {
        this.fullscreen = document.fullscreenElement === this.$component[0];
        m.redraw();
    };

    private onVideoCreated(vnode: VnodeDOM): void {
        const video = vnode.dom as HTMLVideoElement;
        this.video = video;

        video.addEventListener('error', () => this.onMediaError());

        video.addEventListener('loadedmetadata', () => {
            this.duration = Number.isFinite(video.duration) ? video.duration : 0;
            m.redraw();
        });
        video.addEventListener('timeupdate', () => {
            if (!this.seeking) {
                this.currentTime = video.currentTime;
                m.redraw();
            }
        });
        video.addEventListener('play', () => {
            this.playing = true;
            this.scheduleHide();
            m.redraw();
        });
        video.addEventListener('pause', () => {
            this.playing = false;
            this.showControls();
            m.redraw();
        });
        video.addEventListener('ended', () => {
            this.playing = false;
            this.currentTime = 0;
            this.showControls();
            m.redraw();
        });
    }

    private scheduleHide(): void {
        this.clearHideTimer();
        if (this.playing) {
            this.hideTimer = window.setTimeout(() => {
                if (this.$component[0]?.querySelector('.dropdown-menu.show')) {
                    this.scheduleHide();
                    return;
                }
                this.controlsVisible = false;
                m.redraw.sync();
            }, 2000);
        }
    }

    private clearHideTimer(): void {
        if (this.hideTimer !== null) {
            window.clearTimeout(this.hideTimer);
            this.hideTimer = null;
        }
    }

    private showControls(): void {
        this.clearHideTimer();
        this.controlsVisible = true;
    }

    private onPointerMove(): void {
        if (!this.controlsVisible) {
            this.controlsVisible = true;
            m.redraw();
        }
        this.scheduleHide();
    }

    private onKeyDown(e: KeyboardEvent): void {
        if (e.code === 'Space' || e.key === ' ') {
            e.preventDefault();
            this.toggle();
        }
    }

    private toggle(): void {
        if (!this.video) {
            return;
        }
        if (this.playing) {
            this.video.pause();
        } else {
            this.playRequested = true;
            const playPromise = this.video.play();
            if (playPromise) {
                playPromise.catch(() => {
                    if (this.video && isUnsupportedMediaError(this.video)) {
                        this.showDownloadDialog();
                    }
                });
            }
        }
    }

    private onMediaError(): void {
        if (!this.video || !isUnsupportedMediaError(this.video)) {
            return;
        }
        if (this.playRequested) {
            this.showDownloadDialog();
        } else if (!this.unsupported) {
            this.unsupported = true;
            m.redraw();
        }
    }

    private showDownloadDialog(): void {
        if (this.dialogShown) {
            return;
        }
        this.dialogShown = true;
        void showUnsupportedFormatDialog(this.src, this.filename, this.translator);
    }

    private toggleFullscreen(): void {
        const el = this.$component[0];
        if (document.fullscreenElement) {
            void document.exitFullscreen();
        } else if (el) {
            void el.requestFullscreen();
        }
    }

    private seekToEvent(track: HTMLElement, clientX: number): void {
        if (!this.video || this.duration === 0) {
            return;
        }
        const rect = track.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        this.currentTime = ratio * this.duration;
        this.video.currentTime = this.currentTime;
    }

    private onTrackPointerDown(e: PointerEvent): void {
        const track = e.currentTarget as HTMLElement;
        this.seeking = true;
        this.seekToEvent(track, e.clientX);

        const onMove = (ev: PointerEvent) => {
            this.seekToEvent(track, ev.clientX);
            m.redraw();
        };
        const onUp = () => {
            this.seeking = false;
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            m.redraw();
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
    }

    private static formatTime(seconds: number): string {
        const total = Math.floor(seconds);
        const min = Math.floor(total / 60);
        const sec = total % 60;
        return `${min}:${sec.toString().padStart(2, '0')}`;
    }

    public view(): Vnode {
        const ratio = this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0;

        return m('.video-player' + (this.fullscreen ? '.video-player--fullscreen' : ''), {
            tabindex: 0,
            onmousemove: () => this.onPointerMove(),
            onkeydown: (e: KeyboardEvent) => this.onKeyDown(e),
        }, [
            m('video.video-player-video', {
                src: this.src,
                preload: 'metadata',
                playsinline: true,
                disableRemotePlayback: true,
                'x-webkit-airplay': 'deny',
                oncreate: (vnode: VnodeDOM) => this.onVideoCreated(vnode),
                onclick: () => this.toggle(),
            }),
            this.unsupported
                ? renderUnsupportedNotice(this.src, this.filename, this.translator, '.media-player-unsupported--over-video')
                : (this.playing ? null : m('button.video-player-overlay.btn.border-0', {
                    type: 'button',
                    onclick: () => this.toggle(),
                    'aria-label': this.translator.translate('play'),
                }, m('i.bi.bi-play-fill'))),
            this.unsupported
                ? (this.actions
                    ? m('.video-player-controls.video-player-controls--actions-only.d-flex.align-items-center.justify-content-end',
                        renderMediaPlayerActions(this.actions, this.filename, this.translator))
                    : null)
                : m('.video-player-controls.d-flex.align-items-center.gap-2' + (this.controlsVisible ? '' : '.video-player-controls--hidden'), [
                    m('button.video-player-toggle.btn.p-0.border-0', {
                        type: 'button',
                        onclick: () => this.toggle(),
                        'aria-label': this.playing ? this.translator.translate('pause') : this.translator.translate('play'),
                    }, m(`i.bi.bi-${this.playing ? 'pause' : 'play'}-fill`)),
                    m('.video-player-track', {
                        onpointerdown: (e: PointerEvent) => this.onTrackPointerDown(e),
                    }, m('.video-player-track-fill', { style: { width: `${ratio}%` } })),
                    m('.video-player-time', this.duration > 0
                        ? `${VideoPlayer.formatTime(this.currentTime)} / ${VideoPlayer.formatTime(this.duration)}`
                        : '-:-- / -:--'),
                    m('button.video-player-fullscreen.btn.btn-sm', {
                        type: 'button',
                        onclick: () => this.toggleFullscreen(),
                        'aria-label': this.translator.translate('fullscreen'),
                    }, m(`i.bi.bi-${this.fullscreen ? 'fullscreen-exit' : 'arrows-fullscreen'}`)),
                    this.actions ? renderMediaPlayerActions(this.actions, this.filename, this.translator) : null,
                ]),
        ]);
    }
}
